import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    CompsDataGridInfo,
    ConfigurableCompsSelectComponent,
    DataGridComponent,
    FormComponentProps,
    FormType,
} from "../../DynamicFormTypes.ts";
import {
    compareMlsIdsOrAddressesMatch,
    renderComponent,
} from "../../dyanimcFormUtil.tsx";
import {
    AutoFillFieldInfo,
    coerceToCASearchParams,
    CompDetailsRequest,
    CompDetailsResponse,
    CompListResponse,
    POPULATION_DENSITY_MAP,
    CollateralAnalyticsSearchParams,
    POPULATION_DENSITY_MAX_DISTANCE,
    Subject
} from "../../../../api/ApiTypes.ts";
import {CompsMapSearchV2} from "./CompsMapSearchV2.tsx";
import {NotificationType, useNotification} from "../../../../context/NotificationContext.tsx";
import {CompChangedResponse, SelectableComp, SelectedComp, SlotOption} from "./configurableCompsSelectTypes.tsx";
import {useWatch} from "react-hook-form";
import {isEmpty, throttle} from "lodash";
import {useFormApi, usePropertyValuationApi} from "../../../../context/ApiContext.tsx";
import {emitEvent, useEventListener} from "../../../../context/EventBus.ts";
import {useFormInfo, useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {getDefaultRowForDataGrid} from "../../FormRenderer.tsx";
import {ConfirmDeleteComparableModal} from "../../modals/ConfirmDeleteComparableModal.tsx";
import {PropertyDetailsSidebarV2, SelectableCompWithValuation} from "./PropertyDetailSidebarV2.tsx";
import {CompCaImageModal} from "./CompCaImageModal.tsx";
import {loadingManager} from "../../../../context/LoadingManager.ts";

/**
 * THE POINT OF THIS COMPONENT IS FOR THE USER TO SELECT COMPS! THE ONLY SOURCE OF TRUTH IS THE DATA GRID
 * ANY UPDATE MADE TO ANYTHING IS ACTUALLY MADE TO THE DATA GRID, THEN WE LISTEN TO THE DATA GRID CHANGES
 * AND UPDATE THE SELECTED COMPS STATE BASED ON THE DATA GRID VALUES, BECAUSE ON CHANGE IS TRIGGERED ON ANY CHANGE
 * WE USE A THROTTLE TO DO ONE UPDATE AT THE BEGGING OF ALL CHANGES, AND ONE UPDATE EVERY 300ms
 */
const ConfigurableCompsSelectRenderer: React.FC<FormComponentProps<ConfigurableCompsSelectComponent>> = ({
                                                                                                            component
                                                                                                        }) => {
    /**
     * All setting up utilities and data we use in this component in map form for quick look up!
     */
    const notification = useNotification();
    const propertyValuationApi = usePropertyValuationApi();
    const formApi = useFormApi();
    const formInfoContext = useFormInfo();
    const { formMethods, triggerValidation } = useFormMethods();
    const {metaData, formPermissions, schema} = useFormStatic();
    const [changesModalState, setChangesModalState] = React.useState<{
        isOpen: boolean;
        slot?: SelectedComp;
        resolver?: (value: boolean) => void;
    }>({
        isOpen: false
    });
    const [clickedComp, setClickedComp] = useState<SelectableComp | null>(null);
    const [clickedCompImage, setClickedCompImage] = useState<SelectedComp | null>(null);
    const [clickedValuationImages, setClickedValuationImages] = useState<string[] | null>(null);
    const [slotOptions, setSlotOptions] = useState<SlotOption[]>([]);


    const dataGridKeyMap = useMemo((): Record<string, DataGridComponent> => {
        const map: Record<string, DataGridComponent> = {};
        component.components.forEach((nested) => {
            if (nested.type === FormType.DATAGRID) {
                const dg = nested as DataGridComponent;
                map[dg.fieldKey] = dg;
            }
        });
        return map;
    }, [component.components]);

    //map the order of the data grid components
    const dataGridComponentMap = useMemo((): Record<number, DataGridComponent>  => {
        const dgComponents = component.components.filter((nested) => nested.type === FormType.DATAGRID)
            .map(dg => dg as DataGridComponent);
        const map: Record<number, DataGridComponent> = {};
        dgComponents.forEach((dg, index) => {
            map[index] = dg
        });
        return map;
    }, [component.components]);


    const dataGridKeys = useMemo((): string[] => {
        const dataGridKeys: string[] = [];
        Object.entries(dataGridComponentMap).forEach(([_key, value]) => {
            dataGridKeys.push(value.fieldKey)
        });
        return dataGridKeys
    }, [dataGridComponentMap])

    //map for dataGridKey -> datagrid info
    const dataGridInfoMap = useMemo((): Record<string, CompsDataGridInfo> => {
        const map = {} as Record<string, CompsDataGridInfo>;
        dataGridKeys.forEach((dataGridKey) => {
            const info = component.compsDataGridInfos.find((info) => info.dataGridFieldKey === dataGridKey);
            if (!info) {
                console.warn(`ConfigurableCompsSelect: No DataGrid configuration found for field key: ${dataGridKey}`);
            }
            map[info!.dataGridFieldKey] = info!;
        });

        const amountFound = Object.keys(map).length;
        if (amountFound !== dataGridKeys.length) {
            console.warn(`ConfigurableCompsSelect: Expected ${dataGridKeys.length} DataGrid components, but found ${amountFound}.`);
        }

        return map;
    }, [dataGridKeys])

    const valuationDate = useWatch({
        control: formMethods.control,
        name: component.effectiveDateFieldKey ? component.effectiveDateFieldKey : "dummy_key_configurableselect_valuation_date",
        disabled: !component.effectiveDateFieldKey
    })

    /**
     * Actual Logic starts here
     */
    /*set up state and refs*/
    //comps that  are either selected via drag/drop or manually inputted via the data grid
    const [selectedComps, setSelectedComps] = useState<SelectedComp[]>([]);
    //all the comps that are available to choose from (populated via API call in CompsMapSearchV2)
    const [selectableComps, setSelectableComps] = useState<SelectableComp[]>([]);
    const [subject, setSubject] = useState<Subject>();
    const lastRequestSent = useRef("");


    const [searchParams, setSearchParams] =
        useState<CollateralAnalyticsSearchParams>(() => {
            const raw = formMethods.getValues("RAW_SEARCH_PARAMS");
            const defaultDensity = metaData.subjectPropertyPopulationDensity
                ? POPULATION_DENSITY_MAX_DISTANCE[POPULATION_DENSITY_MAP[metaData.subjectPropertyPopulationDensity]].toString()
                : POPULATION_DENSITY_MAX_DISTANCE[metaData.populationDensity].toString();
            return coerceToCASearchParams(raw, defaultDensity);
        });

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchComps = useCallback(async () => {
        try {
            setIsLoading(true);
            if (component.effectiveDateFieldKey && !valuationDate) {
                console.error("We should have the valuation date for sure!!")
                console.error("Not fetching valuation range because valuation date is required but not present", component.effectiveDateFieldKey);
                return;
            }
            console.log("fetching valuation range with date", valuationDate);

            const response: CompListResponse = await formApi.getCompList({
                formSchema: schema,
                metaData: metaData,
                values: formMethods.getValues(),
                address: {
                    addressLine1: metaData.streetAddress,
                    addressLine2: metaData.streetAddressLine2,
                    city: metaData.city,
                    state: metaData.state,
                    zipCode: metaData.zip
                },
                collateralAnalyticsSearchRequest: searchParams,
                valuationDate: valuationDate,
            });

            if (!response || !response.selectableComps || !response.subject) {
                notification.showNotification(NotificationType.ERROR, "No comps found for the given search parameters.");
                setSelectableComps([]);
                return;
            }

            setSelectableComps(response.selectableComps);
            setSubject(response.subject);
        }
        catch (e) {
            console.error(e);
            setSelectableComps([]);
            notification.showNotification(NotificationType.ERROR, "An error occurred while fetching comps.");
        }
        finally {
            setIsLoading(false);
        }
    }, [searchParams, propertyValuationApi, metaData, valuationDate, component]);

    //on change of caRequest, fetch comps
    useEffect(() => {
        // saving to formSubmission object to be able to be used from the deliverable
        formMethods.setValue("RAW_SEARCH_PARAMS", searchParams);
        const requestString = JSON.stringify(searchParams) + valuationDate;
        if (lastRequestSent.current !== requestString) {
            lastRequestSent.current = requestString;
            fetchComps();
        }
    }, [searchParams, valuationDate]);

    const dataGridValues = useWatch({
        //this WATCHES EVERYTHING in the data grid
        control: formMethods.control,
        name: dataGridKeys,
    });

    //never called from data grid field reaction (basically it wipes out the data grid values currently there, and sets the values from the selected comp)
    const getFormValuesFromSelectableComp = useCallback(async (comp: SelectableComp): Promise<Record<string, any>> => {
        //convert the property valuation object to the form values expected by the data grid
        const formValues: any = getDefaultRowForDataGrid(dataGridKeyMap[comp.dgInfo.dataGridFieldKey], metaData);
        //get all the keys from the selected comp and copy them to the form values (we keep these not as actualy form values but for reference)

        if (!comp.propertyValuation) {
            return formValues; //return empty object if propertyValuation is empty
        }

        formValues[comp.dgInfo.prefixForApiPopulation + "MlsId"] = comp.propertyValuation.mlsId;
        formValues[comp.dgInfo.prefixForApiPopulation + "Address"] = comp.propertyValuation.address;
        formValues[comp.dgInfo.prefixForApiPopulation + "LastSoldPrice"] = comp.propertyValuation.lastSoldPrice;
        formValues[comp.dgInfo.prefixForApiPopulation + "ListPrice"] = comp.propertyValuation.listPrice;
        formValues[comp.dgInfo.prefixForApiPopulation + "ImageUrl"] = comp.propertyValuation.imageUrl;
        formValues[comp.dgInfo.prefixForApiPopulation + "AllImages"] = comp.propertyValuation.allImages;

        return formValues;
    }, [selectedComps]);

    // Helper function to convert DataGrid row to PropertyValuation
    const getSelectedCompFromDataGrid = useCallback((formValue: any, dataGridInfo: CompsDataGridInfo, indexOfDg: number): SelectedComp => {
        const selectedComp: SelectedComp = {
            dgInfo: dataGridInfo,
            indexOfDg: indexOfDg
        };
        if (!formValue || typeof formValue !== 'object' || Object.keys(formValue).length === 0) {
            return selectedComp;
        }

        selectedComp.formValues = formValue;
        selectedComp.address = formValue[dataGridInfo.prefixForApiPopulation + "Address"];
        selectedComp.mlsId = formValue[dataGridInfo.prefixForApiPopulation + "MlsId"];
        return selectedComp;
    }, []);

    const debouncedSyncToSelectedComps = useMemo(
        () =>
            throttle(
                //allDataGrids are one for each data grid iteslef so it will be like [0: {datagrid1}, 1: {datagrid2}, ...]
                (allDataGrids: any[]) => {
                    const updatedSelectedComps: SelectedComp[] = [];
                    if (!Array.isArray(allDataGrids)) {
                        console.warn("ConfigurableCompsSelect: dataGridValues is not an array, skipping update.", allDataGrids);
                        return; // Early exit if dataGridValues is not an array
                    }
                    allDataGrids.forEach((dataGrid, index) => {
                        const dataGridComponent = dataGridComponentMap[index];
                        if (!dataGridComponent) {
                            console.warn(`No DataGrid component found for index ${index}`);
                            return; // Skip if no data grid component found for this index
                        }
                        const dataGridInfo = dataGridInfoMap[dataGridComponent.fieldKey]
                        if (!dataGridInfo) {
                            return; // Skip if no data grid info found
                        }
                        if (!Array.isArray(dataGrid)) {
                            return;
                        }
                        dataGrid.forEach((row, indexOfDgRow) => {
                            const selectedComp = getSelectedCompFromDataGrid(row, dataGridInfo, indexOfDgRow);
                            updatedSelectedComps.push(selectedComp);
                        })
                    });
                    setSelectedComps(updatedSelectedComps);
                },
                300,
                { leading: true, trailing: true } // run immediately, then every 300ms until calls stop for 300ms
            ),
        []
    );

    useEffect(() => {
        formMethods.register(component.fieldKey);
    }, [component.fieldKey]);

    useEffect(() => {
        debouncedSyncToSelectedComps(dataGridValues);
        // Cleanup function to cancel pending debounced calls
        return () => {
            debouncedSyncToSelectedComps.cancel();
        };
    }, [dataGridValues]);


    const getSelectedCompByMlsIdOrAddress = (address: string | undefined, mlsId: string | undefined): SelectedComp | undefined => {
        // console.debug("compare:", mlsId, address, selectedComps);
        if (mlsId === undefined && address === undefined) {
            return undefined;
        }
        return selectedComps.find(comp => compareMlsIdsOrAddressesMatch(address, comp.address, mlsId, comp.mlsId));
    }

    const showDeleteConfirmationModal = useCallback((slot: SelectedComp): Promise<boolean> => {
        return new Promise((resolve) => {
            setChangesModalState({
                isOpen: true,
                slot: slot,
                resolver: resolve
            });
        });
    }, []);

    const handleModalClose = useCallback((confirmed: boolean) => {
        if (changesModalState.resolver) {
            changesModalState.resolver(confirmed);
        }
        setChangesModalState({
            ...changesModalState,
            isOpen: false,
        });
    }, [changesModalState.resolver]);


    // Update DataGrid when a comp is dragged and dropped (only from actual code)
    const setSelectedCompAtRow = useCallback(async (comp: SelectableComp, slot: SelectedComp): Promise<CompChangedResponse> => {
        try {
            if (!formPermissions.canEdit) {
                console.warn("Edits are not allowed, skipping setSelectedCompAtRow.");
                return { ok: false, message: "Edits are not allowed." };
            }
            if (!slot) {
                return { ok: false, message: "No slot provided to set selected comp." };
            }
            if (!isEmpty(slot.address)) {
                const deleteConfirmed = await showDeleteConfirmationModal(slot);
                if (!deleteConfirmed) {
                    return {ok: false, message: "Comp not applied"};
                }
            }
            const dataGridKey = comp.dgInfo.dataGridFieldKey;
            const prefix = comp.dgInfo.prefixForApiPopulation;
            const propertyValuation = comp.propertyValuation;
            // Update the form values
            const formValues = await getFormValuesFromSelectableComp(comp);
            const allRows = formMethods.getValues(dataGridKey) || [];
            const isSameAddress = propertyValuation &&
                propertyValuation.address &&
                getSelectedCompByMlsIdOrAddress(propertyValuation.address, propertyValuation.mlsId);
            // If the address is already selected, return an error response
            if (isSameAddress) {
                notification.showNotification(NotificationType.ERROR, `Comp with address ${propertyValuation?.address} is already selected.`);
                return { ok: false, message: `Comp with address ${propertyValuation.address} is already selected.` };
            }

            // set loading to lock down the form
            loadingManager.show();


            if (!propertyValuation) {
                // on delete set defaults for row
                const dgComponent = dataGridKeyMap[dataGridKey];
                allRows[slot.indexOfDg] = getDefaultRowForDataGrid(dgComponent, metaData);
            } else {
                // on new comp, set the basic info showed in drop slot
                // it will get overridden, but its nice to show something while waiting for request
                allRows[slot.indexOfDg] = formValues;
            }
            formMethods.setValue(dataGridKey, allRows);

            if (propertyValuation && propertyValuation.address !== undefined) {
                const request: CompDetailsRequest = {
                    formSchema: schema,
                    metaData: metaData,
                    values: formMethods.getValues(),
                    address: {
                        addressLine1: propertyValuation.address,
                        city: propertyValuation.city!,
                        state: propertyValuation.state!,
                        zipCode: propertyValuation.zip!
                    },
                    forDataGrid: dataGridKey,
                    row: slot.indexOfDg
                };

                const propertyDetailsResponse: CompDetailsResponse = await formApi.getCompDetails(request)
                console.log("Property Details Response:", propertyDetailsResponse);

                if (propertyDetailsResponse && propertyDetailsResponse.autoFillResponse) {
                    const autoFillInfos: Record<string, AutoFillFieldInfo> = propertyDetailsResponse.autoFillResponse?.infos;
                    const autoFillValues: Record<string, string> = propertyDetailsResponse.autoFillResponse?.values;
                    for (const [fieldKey, value] of Object.entries(autoFillValues ?? {})) {
                        formMethods.setValue(fieldKey, value);
                    }
                    formInfoContext.setAutoFillInfos(autoFillInfos);

                    const fetchedValuation = propertyDetailsResponse.propertyDetails.subject ?? undefined;
                    if (fetchedValuation?.comparable?.imageUrl || propertyValuation.imageUrl) {
                        formMethods.setValue(`${dataGridKey}.${slot.indexOfDg}.${prefix}ImageUrl`, fetchedValuation?.comparable?.imageUrl ?? propertyValuation.imageUrl);
                        //to set the imageurl in the image upload component (which will then download it to the form submission)
                        emitEvent("IMAGE_RETRIEVED_FROM_COMPARABLE", {
                            imageUrl: fetchedValuation?.comparable?.imageUrl ?? propertyValuation.imageUrl,
                            index: slot.indexOfDg,
                            dataGridKey: dataGridKey
                        })
                    }
                }
            }
            void triggerValidation();

            return { ok: true, message: `Successfully set selected comp at row ${slot.indexOfDg}` };
        } catch (e) {
            const message = `Error setting selected comp at row ${slot.indexOfDg}: ${e}`;
            notification.showNotification(NotificationType.ERROR, message);
            return { ok: false, message };
        }
        finally {
            // always unlock form to be editable again regardless of failure
            loadingManager.hide();
        }
    }, [getSelectedCompByMlsIdOrAddress, selectedComps]);


    const hasPropertyValuation = (comp: SelectableComp): comp is SelectableCompWithValuation => {
        return comp.propertyValuation !== undefined;
    };


    useEventListener("COMPARABLE_CLICKED", async (event: {
        fieldKey: string;
        index: number;
    }) => {
        try {
            const chosenSlot = selectedComps.find((slot) =>
                slot.dgInfo.dataGridFieldKey === event.fieldKey &&
                slot.indexOfDg === event.index
            );
            const mlsIdName = chosenSlot?.dgInfo.prefixForApiPopulation + "MlsId";
            const mlsId: string = chosenSlot?.formValues?.[mlsIdName];
            const chosenComp = selectableComps.find((slot) =>
                slot.propertyValuation?.mlsId === mlsId
            );
            if (chosenComp != undefined) {
                handleClickComp(chosenComp);
            }
        }
        catch (e) {
            console.error(`[${event}] ERROR:`, e);
        }
    });

    useEventListener("COMPARABLE_IMAGE_CLICKED", async (event: {
        fieldKey: string;
        index: number;
    }) => {
        try {
            const chosenComp = selectedComps.find((slot) =>
                slot.dgInfo.dataGridFieldKey === event.fieldKey &&
                slot.indexOfDg === event.index
            );
            if (chosenComp != undefined) {
                handleClickCompImage(chosenComp);
            }
        }
        catch (e) {
            console.error(`[${event}] ERROR:`, e);
        }
    });

    const changeCompImage = (comp: SelectedComp, newImageUrl: string) => {
        const dgInfo = comp.dgInfo;
        try {
            formMethods.setValue(`${dgInfo.dataGridFieldKey}.${comp.indexOfDg}.${dgInfo.prefixForApiPopulation}ImageUrl`, newImageUrl);

            // Force a re-render of selected comps to reflect changes
            setSelectedComps(selectedComps);

            //to set the imageurl in the image upload component (which will then download it to the form submission)
            emitEvent("IMAGE_RETRIEVED_FROM_COMPARABLE", {
                imageUrl: newImageUrl,
                index: comp.indexOfDg,
                dataGridKey: dgInfo.dataGridFieldKey
            });
        }
        catch (e) {
            console.error(`ERROR:`, e);
            notification.showNotification(
                NotificationType.ERROR,
                "Failed to update comparable image"
            );
        }
    };

    // To Open Flyout
    const handleClickComp = useCallback((comp: SelectableComp) => {
        const options: SlotOption[] = selectedComps
            .filter((selected) => selected.dgInfo.requiredType === comp.dgInfo.requiredType)
            .map((selected) => ({
                label: `${selected.dgInfo.requiredType} Slot ${selected.indexOfDg + 1}`,
                selectedComp: selected
            }));
        setSlotOptions(options);
        setClickedComp(comp);
    }, [selectedComps]);

    // To Open Comp CA Image Modal
    const handleClickCompImage = useCallback((comp: SelectedComp) => {
        if (clickedComp != undefined) {
            setClickedComp(comp);
        }
        setClickedCompImage(comp);
    }, [valuationDate]);

    const handleClickValuationImages = useCallback((images: string[]) => {
        setClickedValuationImages(images);
    }, []);

    // Memoized callback for removing a comp
    const handleRemoveComp = useCallback(async (selected: SelectedComp) => {
        ////essentially set all the fields from propertyvaluatoin to undefined
        const selectAbleComp: SelectableComp = {
            dgInfo: selected.dgInfo,
            propertyValuation: undefined,
        }
        await setSelectedCompAtRow(selectAbleComp, selected);
        formInfoContext.clearDataGridRowInfos(selected.indexOfDg, selected.dgInfo.dataGridFieldKey);
    }, [setSelectedCompAtRow]);


    return (
        <>
            <CompsMapSearchV2
                selectableComps={selectableComps}
                subject={subject}
                selectedComps={selectedComps}
                latitude={parseFloat(metaData.latitude)}
                longitude={parseFloat(metaData.longitude)}
                setSelectedCompAtRow={setSelectedCompAtRow}
                compDefinition={component}
                initialSearchParams={searchParams}
                setSearchParams={setSearchParams}
                isLoading={isLoading}
                handleClickComp={handleClickComp}
                getSelectedCompByMlsIdOrAddress={getSelectedCompByMlsIdOrAddress}
                compareMlsIdsOrAddressesMatch={compareMlsIdsOrAddressesMatch}
                handleRemoveComp={handleRemoveComp}
            />
            {/*this is the grid part possilby other components as well*/}
            {component.components && (
                <div className="p-4 w-full">
                    {component.components.map((nestedComponent, index) =>
                        <React.Fragment key={`configurable-comps-item-${index}-${nestedComponent.fieldKey}`}>
                            {renderComponent({
                                component: nestedComponent,
                            })}
                        </React.Fragment>
                    )}
                </div>
            )}
            <ConfirmDeleteComparableModal
                isOpen={changesModalState.isOpen}
                handleModalClose={handleModalClose}
                slot={changesModalState.slot}
            />
            {clickedComp && hasPropertyValuation(clickedComp) && (
                <PropertyDetailsSidebarV2
                    clickedSelectableComp={clickedComp}
                    options={slotOptions}
                    setClickSelectableComp={setClickedComp}
                    selectedComps={selectedComps}
                    setSelectedCompAtRow={setSelectedCompAtRow}
                    handleClickCompImage={handleClickCompImage}
                    handleClickValuationImages={handleClickValuationImages}
                    valuationDate={valuationDate}
                    orderId={metaData.orderId.toString()}
                />
            )}
            {(clickedCompImage || clickedValuationImages) && (
                <CompCaImageModal
                    clickedCompImage={clickedCompImage ?? undefined}
                    imageListOverride={clickedValuationImages ?? undefined}
                    titleOverride={clickedComp?.propertyValuation?.address}
                    changeCompImage={changeCompImage}
                    setClickSelectableComp={setClickedCompImage}
                    onClose={() => {
                        setClickedCompImage(null);
                        setClickedValuationImages(null);
                    }}
                />
            )}
        </>
    );
}

export const ConfigurableCompsSelect = React.memo(ConfigurableCompsSelectRenderer);
